import type jsPDF from 'jspdf';
import type { CallRecord } from '@/types';
import type { PdfAssets } from '@/lib/pdf/pdfAssets';
import { dispositionBucket } from '@/utils/callRecords';
import {
  CATEGORY_LABELS,
  callCost,
  callingExtension,
  formatRand,
  isOutboundCall,
  summariseCosts,
  type CallCost,
  type CallRates,
  type CostSummary,
  type CostTotals,
  type DestinationCategory,
} from '@/lib/callCosts';
import {
  describeSort,
  resolveColumns,
  sortRows,
  type ReportColumn,
  type ResolvedColumns,
  type SortState,
  type StoredSettings,
} from '@/components/reporting/reportColumns';
import { PDF_COLORS, buildCallCostPdf, type PdfTableSpec, type PdfValue, type Rgb } from '@/components/reporting/callCostPdf';
import type { ReportSection } from './generateReport';

/**
 * The Call Costs report without the screen: costed calls, its two tables' columns, and the CSV
 * and PDF. The Reports page adds on-screen cells to these columns; scheduled reports use them as is.
 */

export const ALL_EXTENSIONS = 'all';
export const FORWARDED = 'forwarded';

export type CallFilter = 'outbound' | 'all';

export interface CostedCall {
  index: number; // position in the fetched records; a stable row key (uids repeat across call legs)
  record: CallRecord;
  cost: CallCost;
  extension: string; // extension number, or FORWARDED
}

export interface ExtensionCostRow {
  extension: string;
  label: string;
  mobile: CostTotals;
  landline: CostTotals;
  total: CostTotals;
}

export const CALL_COLUMNS_KEY = 'callCostReport.columns.calls';
export const EXTENSION_COLUMNS_KEY = 'callCostReport.columns.extensions';
export const CALL_DEFAULT_SORT: SortState = { id: 'time', dir: 'desc' };
export const EXTENSION_DEFAULT_SORT: SortState = { id: 'totalCost', dir: 'desc' };

/** Above this many px of columns the PDF goes landscape. */
const PORTRAIT_MAX_WIDTH = 960;

export function formatDateTime(timeStr: string, timestamp?: number): string {
  try {
    return (timestamp ? new Date(timestamp * 1000) : new Date(timeStr)).toLocaleString();
  } catch {
    return timeStr;
  }
}

export function callTime(record: CallRecord): number {
  return record.timestamp ?? (new Date(record.time).getTime() / 1000 || 0);
}

export function formatDuration(seconds: number): string {
  if (!seconds) return '0:00';
  return `${Math.floor(seconds / 60)}:${String(seconds % 60).padStart(2, '0')}`;
}

export function formatDurationHMS(seconds: number): string {
  const hours = Math.floor(seconds / 3600);
  const minutes = Math.floor((seconds % 3600) / 60);
  const secs = seconds % 60;
  return `${String(hours).padStart(2, '0')}:${String(minutes).padStart(2, '0')}:${String(secs).padStart(2, '0')}`;
}

export function formatRate(rate: number | null) {
  return rate === null ? 'Not priced' : `${formatRand(rate)}/min`;
}

const MONTHS = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];

/** "1 Sep 2026" from "2026-09-01". */
export function formatDay(isoDate: string) {
  const [year, month, day] = isoDate.split('-').map(Number);
  return `${day} ${MONTHS[month - 1]} ${year}`;
}

/** "16 Oct 2026, 21:08" */
export function formatGenerated(date: Date) {
  const time = `${String(date.getHours()).padStart(2, '0')}:${String(date.getMinutes()).padStart(2, '0')}`;
  return `${date.getDate()} ${MONTHS[date.getMonth()]} ${date.getFullYear()}, ${time}`;
}

/** "Siphokazi Kula (3126)" from "Siphokazi Kula<3126>", on one line for the PDF and CSV. */
export function partyText(value?: string) {
  const raw = String(value || '').trim();
  const match = raw.match(/^(.*?)\s*<([^>]*)>\s*$/);
  if (!match) return raw || '-';
  const [, name, number] = match;
  if (!name || name.replace(/\D/g, '') === number.replace(/\D/g, '')) return number || name || '-';
  return `${name} (${number})`;
}

const emptyTotals = (): CostTotals => ({ calls: 0, seconds: 0, cost: 0 });

export function applyCallFilter(calls: CostedCall[], filter: CallFilter) {
  return filter === 'outbound' ? calls.filter((c) => isOutboundCall(c.record)) : calls;
}

export function sumCost(calls: CostedCall[]) {
  return Math.round(calls.reduce((sum, c) => sum + c.cost.cost * 100, 0)) / 100;
}

export function sumTalk(calls: CostedCall[]) {
  return calls.reduce((sum, c) => sum + Math.max(0, Math.round(c.record.talk_duration || 0)), 0);
}

function addToTotals(totals: CostTotals, cost: CallCost) {
  totals.calls += 1;
  totals.seconds += cost.billableSeconds;
  totals.cost = Math.round((totals.cost + cost.cost) * 100) / 100;
}

function tableWidth<Row>(table: ResolvedColumns<Row>, columns: ReportColumn<Row>[]) {
  return columns.reduce((sum, column) => sum + table.widthOf(column), 0);
}

/** Every call priced; in "All extensions" each call is put under the extension that made it. */
export function costCalls(records: CallRecord[], rates: CallRates, reportExtension: string): CostedCall[] {
  return records.map((record, index) => ({
    index,
    record,
    cost: callCost(record, rates),
    extension: reportExtension !== ALL_EXTENSIONS ? reportExtension : (callingExtension(record) ?? FORWARDED),
  }));
}

/** "Cost by extension": charged calls per extension, dearest first. */
export function extensionCostRows(calls: CostedCall[], label: (extension: string) => string): ExtensionCostRow[] {
  const rows = new Map<string, ExtensionCostRow>();
  for (const call of calls) {
    if (!call.cost.category || call.cost.billableSeconds === 0) continue;
    const row =
      rows.get(call.extension) ??
      ({
        extension: call.extension,
        label: '',
        mobile: emptyTotals(),
        landline: emptyTotals(),
        total: emptyTotals(),
      } satisfies ExtensionCostRow);
    if (call.cost.category === 'mobile') addToTotals(row.mobile, call.cost);
    if (call.cost.category === 'landline') addToTotals(row.landline, call.cost);
    addToTotals(row.total, call.cost);
    rows.set(call.extension, row);
  }
  return Array.from(rows.values())
    .map((row) => ({ ...row, label: label(row.extension) }))
    .sort((a, b) => b.total.cost - a.total.cost || a.extension.localeCompare(b.extension));
}

/** "1001 - Reception", "All extensions" or "Forwarded / other". */
export function costExtensionLabel(number: string, names: Map<string, string>) {
  return number === FORWARDED
    ? 'Forwarded / other'
    : number === ALL_EXTENSIONS
      ? 'All extensions'
      : `${number} - ${names.get(number) ?? ''}`.replace(/ - $/, '');
}

/** Totals for the calls table, by column id. */
export function callFooter(calls: CostedCall[]): Record<string, string> {
  return { talk: formatDurationHMS(sumTalk(calls)), cost: formatRand(sumCost(calls)) };
}

/** Totals for the "Cost by extension" table, by column id. */
export function extensionFooter(summary: CostSummary): Record<string, string> {
  const { mobile, landline } = summary.byCategory;
  return {
    calls: String(summary.total.calls),
    mobileCalls: String(mobile.calls),
    mobileTime: formatDurationHMS(mobile.seconds),
    mobileCost: formatRand(mobile.cost),
    landlineCalls: String(landline.calls),
    landlineTime: formatDurationHMS(landline.seconds),
    landlineCost: formatRand(landline.cost),
    totalTime: formatDurationHMS(summary.total.seconds),
    totalCost: formatRand(summary.total.cost),
  };
}

export function costNote(summary: CostSummary) {
  const tollFree = summary.byCategory.tollFree.calls;
  const unpriced = summary.unpricedCalls;
  return [
    `Answered outbound calls only, billed per second on talk time. Toll-free (080) calls are free${tollFree > 0 ? ` (${tollFree} calls)` : ''}.`,
    unpriced > 0
      ? `${unpriced} international or unrecognised number${unpriced === 1 ? ' is' : 's are'} not priced (marked "Not priced").`
      : '',
  ]
    .filter(Boolean)
    .join(' ');
}

export const statusPdfColors: Record<ReturnType<typeof dispositionBucket>, Rgb> = {
  answered: [21, 128, 61],
  noAnswer: [194, 65, 12],
  busy: [194, 65, 12],
  failed: PDF_COLORS.muted,
};

export const categoryPdfColors: Record<DestinationCategory, Rgb> = {
  mobile: PDF_COLORS.accentDark,
  landline: PDF_COLORS.indigoDark,
  tollFree: PDF_COLORS.muted,
  international: PDF_COLORS.amber,
  other: PDF_COLORS.amber,
};

/** The calls table's columns, without on-screen cells. */
export function costCallColumns(options: {
  showExtensionColumn: boolean;
  extensionLabel: (extension: string) => string;
  formatTime?: (record: CallRecord) => string;
}): ReportColumn<CostedCall>[] {
  const formatTime = options.formatTime ?? ((record: CallRecord) => formatDateTime(record.time, record.timestamp));
  const seconds = (
    id: string,
    label: string,
    pick: (record: CallRecord) => number | undefined,
    hiddenByDefault = false
  ): ReportColumn<CostedCall> => ({
    id,
    label,
    width: 104,
    align: 'right',
    firstSort: 'desc',
    hiddenByDefault,
    sortValue: ({ record }) => pick(record) || 0,
    text: ({ record }) => formatDuration(Math.round(pick(record) || 0)),
  });

  return [
    {
      id: 'time',
      label: 'Date & time',
      width: 212,
      firstSort: 'desc',
      sortValue: ({ record }) => callTime(record),
      text: ({ record }) => formatTime(record),
    },
    ...(options.showExtensionColumn
      ? [
          {
            id: 'extension',
            label: 'Extension',
            width: 190,
            sortValue: ({ extension }) => options.extensionLabel(extension),
            text: ({ extension }) => options.extensionLabel(extension),
          } satisfies ReportColumn<CostedCall>,
        ]
      : []),
    {
      id: 'type',
      label: 'Type',
      width: 96,
      hiddenByDefault: true,
      sortValue: ({ record }) => record.call_type,
      text: ({ record }) => record.call_type,
    },
    {
      id: 'from',
      label: 'From',
      width: 150,
      sortValue: ({ record }) => record.call_from || '',
      text: ({ record }) => partyText(record.call_from),
    },
    {
      id: 'to',
      label: 'To',
      width: 160,
      sortValue: ({ record }) => record.call_to || '',
      text: ({ record }) => partyText(record.call_to),
    },
    {
      id: 'callerId',
      label: 'Caller ID sent',
      width: 136,
      hiddenByDefault: true,
      sortValue: ({ record }) => record.dod_number || '',
      text: ({ record }) => record.dod_number || '-',
    },
    {
      id: 'trunk',
      label: 'Trunk',
      width: 150,
      hiddenByDefault: true,
      sortValue: ({ record }) => record.dst_trunk || record.src_trunk || '',
      text: ({ record }) => record.dst_trunk || record.src_trunk || '-',
    },
    {
      id: 'status',
      label: 'Status',
      width: 124,
      sortValue: ({ record }) => record.disposition || '',
      text: ({ record }) => record.disposition || 'Unknown',
    },
    {
      id: 'destination',
      label: 'Destination',
      width: 124,
      sortValue: ({ record, cost }) => (cost.category ? CATEGORY_LABELS[cost.category] : record.call_type),
      text: ({ record, cost }) => (cost.category ? CATEGORY_LABELS[cost.category] : record.call_type),
    },
    seconds('ring', 'Ring time', (record) => record.ring_duration, true),
    {
      ...seconds('talk', 'Talk time', (record) => record.talk_duration),
      csv: {
        headers: ['Talk time', 'Talk seconds'],
        values: ({ record }) => {
          const talk = Math.max(0, Math.round(record.talk_duration || 0));
          return [formatDuration(talk), talk];
        },
      },
    },
    {
      id: 'rate',
      label: 'Rate',
      width: 112,
      align: 'right',
      firstSort: 'desc',
      sortValue: ({ cost }) => (cost.category && cost.billableSeconds > 0 ? (cost.ratePerMinute ?? -1) : -2),
      text: ({ cost }) => (cost.category && cost.billableSeconds > 0 ? formatRate(cost.ratePerMinute) : ''),
      csv: {
        headers: ['Rate (R/min)'],
        values: ({ cost }) => [cost.ratePerMinute === null ? '' : cost.ratePerMinute.toFixed(2)],
      },
    },
    {
      id: 'cost',
      label: 'Cost',
      width: 104,
      align: 'right',
      firstSort: 'desc',
      sortValue: ({ cost }) => cost.cost,
      text: ({ cost }) => (cost.category ? formatRand(cost.cost) : '-'),
      csv: { headers: ['Cost (R)'], values: ({ cost }) => [cost.category ? cost.cost.toFixed(2) : ''] },
    },
  ];
}

/** The "Cost by extension" table's columns, without on-screen cells. */
export function costExtensionColumns(): ReportColumn<ExtensionCostRow>[] {
  const numberColumn = (
    id: string,
    label: string,
    value: (row: ExtensionCostRow) => number,
    format: (value: number) => string,
    options: { hidden?: boolean; width?: number } = {}
  ): ReportColumn<ExtensionCostRow> => ({
    id,
    label,
    width: options.width ?? 124,
    align: 'right',
    firstSort: 'desc',
    hiddenByDefault: options.hidden,
    sortValue: value,
    text: (row) => format(value(row)),
  });

  return [
    {
      id: 'extension',
      label: 'Extension',
      // Stretches to fill spare width; small enough that the table fits beside the sidebar at 1440px
      width: 190,
      sortValue: (row) => row.label,
      text: (row) => row.label,
    },
    numberColumn('calls', 'Calls', (row) => row.total.calls, String, { width: 84 }),
    numberColumn('mobileCalls', 'Mobile calls', (row) => row.mobile.calls, String, { hidden: true }),
    numberColumn('mobileTime', 'Mobile time', (row) => row.mobile.seconds, formatDurationHMS),
    numberColumn('mobileCost', 'Mobile cost', (row) => row.mobile.cost, formatRand),
    numberColumn('landlineCalls', 'Landline calls', (row) => row.landline.calls, String, { hidden: true }),
    numberColumn('landlineTime', 'Landline time', (row) => row.landline.seconds, formatDurationHMS),
    numberColumn('landlineCost', 'Landline cost', (row) => row.landline.cost, formatRand),
    numberColumn('totalTime', 'Total time', (row) => row.total.seconds, formatDurationHMS, { hidden: true }),
    numberColumn('totalCost', 'Total cost', (row) => row.total.cost, formatRand),
  ];
}

/** CSV with the given columns, a totals row at the end. Starts with a byte-order mark for Excel. */
export function costCsv(calls: CostedCall[], columns: ReportColumn<CostedCall>[]): string {
  const csvOf = (column: ReportColumn<CostedCall>) =>
    column.csv ?? { headers: [column.label], values: (call: CostedCall) => [column.text(call)] };
  const escape = (value: string | number) => {
    const text = String(value ?? '');
    return /[",\n]/.test(text) ? `"${text.replace(/"/g, '""')}"` : text;
  };
  const header = columns.flatMap((column) => csvOf(column).headers);
  const rows: Array<Array<string | number>> = calls.map((call) => columns.flatMap((column) => csvOf(column).values(call)));
  const talk = sumTalk(calls);
  const totals = columns.flatMap((column) => {
    if (column.id === 'cost') return [sumCost(calls).toFixed(2)];
    if (column.id === 'talk') return [formatDurationHMS(talk), talk];
    return csvOf(column).headers.map(() => '');
  });
  if (totals[0] === '') totals[0] = 'Total';
  rows.push(totals);
  const csv = [header, ...rows].map((row) => row.map(escape).join(',')).join('\r\n');
  return `${String.fromCharCode(0xfeff)}${csv}`;
}

export interface CostExportInput {
  records: CallRecord[];
  /** The report's extension number, or ALL_EXTENSIONS. */
  reportExtension: string;
  /** One extension of an "All extensions" report (its drill-down), or null for the whole report. */
  focus: string | null;
  rates: CallRates;
  callFilter: CallFilter;
  callColumns: StoredSettings;
  extensionColumns: StoredSettings;
  extensionNames: Map<string, string>;
  startDate: string;
  endDate: string;
  customerName?: string;
  assets?: PdfAssets;
  now?: Date;
  formatTime?: (record: CallRecord) => string;
}

export interface CostExport {
  fileBase: string;
  summary: CostSummary;
  /** Calls in the list (after the outbound/all filter). */
  listed: number;
  csv: () => string;
  pdf: () => jsPDF;
  /** Tables for an email body. */
  sections: () => ReportSection[];
}

/** The CSV and PDF of a Call Costs report, or of one extension in it, with the chosen columns and sort. */
export function costExport(input: CostExportInput): CostExport {
  const extensionLabel = (number: string) => costExtensionLabel(number, input.extensionNames);
  const allMode = input.reportExtension === ALL_EXTENSIONS;
  const costed = costCalls(input.records, input.rates, input.reportExtension);
  const scopeCalls = input.focus ? costed.filter((c) => c.extension === input.focus) : costed;
  const summary = summariseCosts(scopeCalls.map((c) => c.record), input.rates);

  const callTable = resolveColumns(
    costCallColumns({ showExtensionColumn: allMode, extensionLabel, formatTime: input.formatTime }),
    input.callColumns,
    CALL_DEFAULT_SORT
  );
  const extensionTable = resolveColumns(costExtensionColumns(), input.extensionColumns, EXTENSION_DEFAULT_SORT);
  // The Extension column is left out of one extension's own export
  const chosen = callTable.visible.filter((column) => !(input.focus && column.id === 'extension'));
  const columns = chosen.length ? chosen : callTable.columns.slice(0, 1);
  const calls = sortRows(applyCallFilter(scopeCalls, input.callFilter), callTable.sortColumn, callTable.sort.dir);

  const target = input.focus ?? input.reportExtension;
  const who =
    target === ALL_EXTENSIONS
      ? 'All_Extensions'
      : target === FORWARDED
        ? 'Forwarded'
        : `Ext_${target}${input.extensionNames.get(target) ? `_${input.extensionNames.get(target)}` : ''}`;
  const fileBase = `Call_Cost_Report_${who.replace(/[^\w.-]+/g, '_')}_${input.startDate}_to_${input.endDate}`;

  const pdf = () => {
    const extensionRows = extensionCostRows(costed, extensionLabel);
    const extColumns = extensionTable.visible;
    const includeExtensions = allMode && !input.focus && extensionRows.length > 1;
    const totalRow = <Row>(cols: ReportColumn<Row>[], footer: Record<string, string>, strongId: string) => {
      const values: PdfValue[] = cols.map((column) =>
        column.id === strongId ? { text: footer[column.id] ?? '', color: PDF_COLORS.accentDark } : (footer[column.id] ?? '')
      );
      if (values[0] === '') values[0] = 'Total';
      return values;
    };
    const pdfColumns = <Row>(table: ResolvedColumns<Row>, cols: ReportColumn<Row>[]) =>
      cols.map((column) => ({ header: column.label, width: table.widthOf(column), align: column.align }));

    const callCell = (column: ReportColumn<CostedCall>, call: CostedCall): PdfValue => {
      const value = column.text(call);
      if (column.id === 'destination' && call.cost.category) {
        return { text: value, color: categoryPdfColors[call.cost.category], badge: true };
      }
      if (column.id === 'status') {
        return { text: value, color: statusPdfColors[dispositionBucket(call.record)], badge: true };
      }
      if (column.id === 'cost') return { text: value, bold: true, color: PDF_COLORS.ink };
      return value;
    };

    const tables: PdfTableSpec[] = [];
    if (includeExtensions) {
      tables.push({
        title: 'Cost by extension',
        meta: `${extensionRows.length} extensions, ${describeSort(extensionTable)}`,
        columns: pdfColumns(extensionTable, extColumns),
        rows: sortRows(extensionRows, extensionTable.sortColumn, extensionTable.sort.dir).map((row) =>
          extColumns.map((column) =>
            column.id === 'totalCost' ? { text: column.text(row), bold: true, color: PDF_COLORS.ink } : column.text(row)
          )
        ),
        total: totalRow(extColumns, extensionFooter(summary), 'totalCost'),
      });
    }
    tables.push({
      title: input.callFilter === 'outbound' ? 'Outbound calls' : 'All calls',
      meta: `${calls.length} call${calls.length === 1 ? '' : 's'}, ${describeSort(callTable)}`,
      columns: pdfColumns(callTable, columns),
      rows: calls.map((call) => columns.map((column) => callCell(column, call))),
      total: totalRow(columns, callFooter(calls), 'cost'),
    });

    const widest = Math.max(
      tableWidth(callTable, columns),
      includeExtensions ? tableWidth(extensionTable, extColumns) : 0
    );
    const { mobile, landline } = summary.byCategory;
    return buildCallCostPdf({
      orientation: widest <= PORTRAIT_MAX_WIDTH ? 'portrait' : 'landscape',
      assets: input.assets,
      customerName: input.customerName,
      heading: extensionLabel(target),
      period: `${formatDay(input.startDate)} - ${formatDay(input.endDate)}`,
      generated: `Generated ${formatGenerated(input.now ?? new Date())}`,
      chips: [
        `Mobile ${formatRand(input.rates.mobilePerMinute)}/min`,
        `Landline ${formatRand(input.rates.landlinePerMinute)}/min`,
        'Billed per second',
        'Toll-free (080) free',
      ],
      cards: [
        {
          label: 'Total cost',
          value: formatRand(summary.total.cost),
          detail: `${summary.total.calls} charged calls`,
          featured: true,
        },
        {
          label: 'Mobile',
          value: formatRand(mobile.cost),
          detail: `${mobile.calls} calls, ${formatDurationHMS(mobile.seconds)}`,
          color: PDF_COLORS.accent,
        },
        {
          label: 'Landline',
          value: formatRand(landline.cost),
          detail: `${landline.calls} calls, ${formatDurationHMS(landline.seconds)}`,
          color: PDF_COLORS.indigo,
        },
        {
          label: 'Charged talk time',
          value: formatDurationHMS(summary.total.seconds),
          detail: `${scopeCalls.filter((c) => isOutboundCall(c.record)).length} outbound calls in total`,
        },
      ],
      split: [
        { label: 'Mobile', amount: mobile.cost, display: formatRand(mobile.cost), color: PDF_COLORS.accent },
        { label: 'Landline', amount: landline.cost, display: formatRand(landline.cost), color: PDF_COLORS.indigo },
      ],
      note: costNote(summary),
      tables,
    });
  };

  const sections = (): ReportSection[] => {
    const result: ReportSection[] = [];
    const extensionRows = extensionCostRows(costed, extensionLabel);
    if (allMode && !input.focus && extensionRows.length > 1) {
      const footer = extensionFooter(summary);
      result.push({
        title: 'Cost by extension',
        meta: `${extensionRows.length} extensions`,
        role: 'summary',
        columns: extensionTable.visible.map((column) => ({ header: column.label, align: column.align === 'right' ? 'right' : undefined })),
        rows: sortRows(extensionRows, extensionTable.sortColumn, extensionTable.sort.dir).map((row) =>
          extensionTable.visible.map((column) => (column.id === 'totalCost' ? { text: column.text(row), bold: true } : column.text(row)))
        ),
        total: extensionTable.visible.map((column, index) => footer[column.id] ?? (index === 0 ? 'Total' : '')),
      });
    }
    const footer = callFooter(calls);
    result.push({
      title: input.callFilter === 'outbound' ? 'Outbound calls' : 'All calls',
      meta: `${calls.length} call${calls.length === 1 ? '' : 's'}, ${describeSort(callTable)}`,
      role: 'detail',
      columns: columns.map((column) => ({ header: column.label, align: column.align === 'right' ? 'right' : undefined })),
      rows: calls.map((call) => columns.map((column) => column.text(call))),
      total: columns.map((column, index) => footer[column.id] ?? (index === 0 ? 'Total' : '')),
      empty: 'No calls.',
    });
    return result;
  };

  return { fileBase, summary, listed: calls.length, csv: () => costCsv(calls, columns), pdf, sections };
}
